import WorkoutPreviousSets from "@/components/fitness/WorkoutPreviousSets";
import { clearFreeWorkoutSetup } from "@/lib/fitness/freeWorkoutSelection";
import { clearFreeWorkoutBuilder, clearFreeWorkoutOrder } from "@/lib/fitness/freeWorkoutBuilder";
import WorkoutCancelButton from "@/components/fitness/WorkoutCancelButton";
import { FITNESS_INTERVAL_LIMITS, fitnessIntervalKind } from "@/lib/fitnessIntervalProtocol";
import { fitnessExercisePresentationIdentity, fitnessExercisePresentationName } from "@/lib/fitnessExercisePresentation";
import { MUSCLE_GROUP_MAP } from "@/lib/fitnessMuscleGroups";
import { prepareFreeWorkoutExercises, readWorkoutPlanDays, preparePlannedWorkoutExercise, readLastWorkoutWeight, type WorkoutPlanDayInput } from "@/lib/fitness/workoutPreparation";
import { createFitnessId } from "@/lib/fitnessId";
import { getGymVolumeKg } from "@/lib/fitness/sessionKind";
import React, { useState, useEffect, useCallback, useRef, useMemo, useSyncExternalStore } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import MobileLayout from "@/components/MobileLayout";
import { sfx } from "@/lib/sfx";
import { X, Pause, Play } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";
import { supabase } from "@/integrations/supabase/client";
import { useXP } from "@/hooks/useXP";
import { getExerciseByName, localizeExerciseName } from "@/lib/fitnessApi";
import { useExerciseMediaBatch } from "@/hooks/useExerciseMedia";
import { toast } from "sonner";
import { getWorkoutDraft } from "@/lib/fitnessDraft";
import { ensureWorkoutDraftHydrated, flushWorkoutDraftPersistence, getFitnessDraftPersistenceStatus,
  retryWorkoutDraftPersistence, subscribeFitnessDraftPersistence } from "@/lib/fitnessDraftPersistence";
import WorkoutDraftPersistenceStatus from "@/components/fitness/WorkoutDraftPersistenceStatus";
import { prepareFitnessWorkoutFinish, confirmFitnessWorkoutFinish, acknowledgeFitnessWorkoutFinishReceipt,
  type FitnessWorkoutFinishOptions, type FitnessWorkoutFinishReceipt } from "@/lib/fitnessWorkoutFinishWriter";
import { useFitnessWorkoutRuntime } from "@/hooks/useFitnessWorkoutRuntime";
import { fitnessWorkoutController, type FitnessWorkoutState, type FitnessWorkoutExercise, type FitnessWorkoutSet } from "@/lib/fitnessWorkoutController";
import { useFitnessResource } from "@/hooks/useFitnessResource";
import FitnessDataState from "@/components/fitness/FitnessDataState";
import { nextPlanDay } from "@/lib/fitnessData";
import { analytics } from "@/lib/analytics";
import WorkoutCountdown from "@/components/fitness/WorkoutCountdown";
import WorkoutRestTimer from "@/components/fitness/WorkoutRestTimer";
import WorkoutCompletion from "@/components/fitness/WorkoutCompletion";
import WorkoutSetup from "@/components/fitness/WorkoutSetup";
import GuidedWorkoutPreparation from "@/components/fitness/GuidedWorkoutPreparation";
import ExerciseDemonstration from "@/components/fitness/ExerciseDemonstration";
import { createFitnessGuidedPreparation, isGuidedPreparationBlocking, transitionFitnessGuidedPreparation, type FitnessGuidedPreparationAction } from "@/lib/fitnessGuidedPreparation";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import WorkoutSetVoiceInput from "@/components/fitness/WorkoutSetVoiceInput";
import WorkoutExecution from "@/components/fitness/WorkoutExecution";
import { getWorkoutSetTransition } from "@/lib/fitness/workoutSchedule";
import { useFitnessPreferences } from "@/hooks/useFitnessPreferences";
import FitnessInstructorSelector from "@/components/fitness/FitnessInstructorSelector";
import FitnessExerciseNotes from "@/components/fitness/FitnessExerciseNotes";
import FitnessSetMetadataInput from "@/components/fitness/FitnessSetMetadataInput";
import FitnessWorkoutSequence from "@/components/fitness/FitnessWorkoutSequence";
import WorkoutExerciseReplacement from "@/components/fitness/WorkoutExerciseReplacement";
import { replaceCurrentWorkoutExercise } from "@/lib/fitness/replacement";
import { getFitnessWorkoutExperienceCopy } from "@/lib/fitnessWorkoutExperienceCopy";

/* ─── Types ─── */
type SetData = FitnessWorkoutSet;
type ExerciseData = FitnessWorkoutExercise;

/* ─── State machine types ─── */
export interface ExerciseConfig {
  weight: number;
  reps: number;
  sets: number;
}

export type WorkoutPhase =
  | "setup"
  | "countdown"
  | "execution"
  | "rest"
  | "exercise-complete"
  | "complete";

const SB_FITNESS_BG = "https://lluvykhqgdnbfobjixbj.supabase.co/storage/v1/object/public/Modulo%20Fittness/Academia%20-%20treino%20de%20hoje.jpg";

const readWorkoutIdentity = async () => true;
const FITNESS_RUNTIME_READY_TIMEOUT_MS = 3_000;

/* ─── Main component ─── */
const FitnessWorkout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, tf, lang } = useLanguage();
  const experienceCopy = getFitnessWorkoutExperienceCopy(lang);
  const { addXP } = useXP();
  const identity = useFitnessResource(readWorkoutIdentity);
  const checkpointOwnerId = identity.userId, captureCheckpointOwner = identity.capture;
  const instructor = useFitnessPreferences(identity.userId);
  const persistenceSnapshot = useCallback(() => getFitnessDraftPersistenceStatus(identity.userId), [identity.userId]);
  const draftPersistence = useSyncExternalStore(subscribeFitnessDraftPersistence, persistenceSnapshot, persistenceSnapshot);
  const [draftRetrying, setDraftRetrying] = useState(false);
  const [closingCheckpoint, setClosingCheckpoint] = useState(false);
  const persistenceAction = useRef(0);
  const closeInFlight = useRef(false);
  useEffect(() => {
    const invalidate = () => { persistenceAction.current++; closeInFlight.current = false; };
    invalidate(); setDraftRetrying(false); setClosingCheckpoint(false);
    return invalidate;
  }, [identity.userId]);
  const workoutRuntime = useFitnessWorkoutRuntime(identity.userId);
  const runtimeState = workoutRuntime.state;
  const runtimeField = workoutRuntime.field;
  const runtimeRunId = workoutRuntime.runtime?.runId;
  const runtimeEpoch = workoutRuntime.runtime?.epoch;
  const runtimeLifecycle = workoutRuntime.runtime?.lifecycle;
  const getMountedRun = useCallback(() => {
    const current = identity.userId && fitnessWorkoutController.getOwned(identity.userId);
    return current && current.runId === runtimeRunId && current.epoch === runtimeEpoch && current.lifecycle === runtimeLifecycle ? current : null;
  }, [identity.userId, runtimeRunId, runtimeEpoch, runtimeLifecycle]);
  const paused = workoutRuntime.runtime?.paused ?? false;
  const runtimeBusy = !!workoutRuntime.runtime?.busy;
  const guidedPreparation = runtimeState.guidedPreparation;
  const preparingGuided = isGuidedPreparationBlocking(guidedPreparation);
  const handleGuidedPreparation = useCallback((action: FitnessGuidedPreparationAction) => {
    const mounted = getMountedRun();
    if (!identity.userId || !mounted || mounted.paused || mounted.busy || mounted.state.phase !== "setup"
      || !mounted.state.guidedPreparation || action === "tick" && document.hidden) return;
    const next = transitionFitnessGuidedPreparation(mounted.state.guidedPreparation, action);
    if (next !== mounted.state.guidedPreparation) fitnessWorkoutController.patch(identity.userId, { guidedPreparation: next });
  }, [identity.userId, getMountedRun]);
  const [isSaving, setIsSaving] = useState(false);
  const [finalizeError, setFinalizeError] = useState(false);
  const finalizing = useRef<string | null>(null);
  const finishAcknowledgment = useRef<{ options: FitnessWorkoutFinishOptions; receipt: FitnessWorkoutFinishReceipt; started: boolean } | null>(null);
  const [finishReceipt, setFinishReceipt] = useState<FitnessWorkoutFinishReceipt | null>(null);
  const restDeadline = runtimeState.restDeadline, restDuration = runtimeState.restDuration;
  const setRestDeadline = useCallback((value: number | null) => runtimeField("restDeadline", value), [runtimeField]);
  const setRestDuration = useCallback((value: number) => runtimeField("restDuration", value), [runtimeField]);
  const sessionKeyRef = useRef(runtimeState.clientId);
  sessionKeyRef.current = runtimeState.clientId;
  const draftOwner = useRef<string | null>(null);
  const lastResolvedOwner = useRef<string | null>(null);

  /* The controller is the single source of workout state across routes. */
  const phase = runtimeState.phase, currentExerciseIndex = runtimeState.currentExerciseIndex, currentSet = runtimeState.currentSet;
  const exerciseConfigs = runtimeState.exerciseConfigs, exercises = runtimeState.exercises;
  const setPhase = useCallback((value: WorkoutPhase) => runtimeField("phase", value), [runtimeField]);
  const setCurrentExerciseIndex = useCallback((value: number | ((before: number) => number)) => runtimeField("currentExerciseIndex", value), [runtimeField]);
  const setCurrentSet = useCallback((value: number | ((before: number) => number)) => runtimeField("currentSet", value), [runtimeField]);
  const setExerciseConfigs = useCallback((value: Record<number, ExerciseConfig> | ((before: Record<number, ExerciseConfig>) => Record<number, ExerciseConfig>)) => runtimeField("exerciseConfigs", value), [runtimeField]);
  const setExercises = useCallback((value: ExerciseData[] | ((before: ExerciseData[]) => ExerciseData[])) => runtimeField("exercises", value), [runtimeField]);

  // Block double-submits on FEITO button — prevents stale-closure race where
  // two rapid clicks both read the same currentSet and both increment it.
  const setDoneInFlightRef = useRef<boolean>(false);
  const workoutName = runtimeState.workoutName;
  const setWorkoutName = useCallback((value: string) => runtimeField("workoutName", value), [runtimeField]);
  const planId = runtimeState.planId;
  const setPlanId = useCallback((value: string | null) => runtimeField("planId", value), [runtimeField]);
  const sessionId = runtimeState.sessionId;
  const setSessionId = useCallback((value: string | null) => runtimeField("sessionId", value), [runtimeField]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [showResume, setShowResume] = useState(false);
  const xpTotal = runtimeState.xpTotal;
  const setXpTotal = useCallback((value: number | ((before: number) => number)) => runtimeField("xpTotal", value), [runtimeField]);

  // Per-set weight/reps editing (Voice MVP completion phase).
  // editingWeight/editingReps reflect the LIVE values the user sees in
  // WorkoutExecution and that handleSetDone persists. actualSetValues caches
  // completed-set values per exercise so set N+1 carry-overs from set N
  // (progressive overload UX). In-memory only — no extra column needed since
  // exercise_logs.sets[] already stores final weight/reps per set.
  const actualSetValues = runtimeState.actualSetValues;
  const setActualSetValues = useCallback((value: Record<number, { weight: number; reps: number }[]> | ((before: Record<number, { weight: number; reps: number }[]>) => Record<number, { weight: number; reps: number }[]>)) => runtimeField("actualSetValues", value), [runtimeField]);
  const editingWeight = runtimeState.editingWeight;
  const setEditingWeight = useCallback((value: number | ((before: number) => number)) => runtimeField("editingWeight", value), [runtimeField]);
  const editingReps = runtimeState.editingReps;
  const setEditingReps = useCallback((value: number | ((before: number) => number)) => runtimeField("editingReps", value), [runtimeField]);
  const [showEditSheet, setShowEditSheet] = useState<boolean>(false);
  const voiceEditorActive = useRef(false);
  voiceEditorActive.current = showEditSheet && phase === "execution" && !paused && !runtimeBusy;
  useEffect(() => () => { voiceEditorActive.current = false; }, []);
  const voiceOwnerId = identity.userId, captureVoiceOwner = identity.capture;
  const voiceOwnerGuard = useMemo(() => {
    if (!voiceOwnerId) return null;
    const ownerCurrent = captureVoiceOwner();
    const run = getMountedRun();
    if (!run || run.lifecycle !== runtimeLifecycle) return null;
    const lifecycle = runtimeLifecycle;
    return { ownerId: voiceOwnerId, isCurrent: () => {
      const current = getMountedRun();
      return ownerCurrent() && voiceEditorActive.current && !!current && current.lifecycle === lifecycle
        && !current.paused && !current.busy && current.state.phase === "execution"
        && current.state.currentExerciseIndex === currentExerciseIndex && current.state.currentSet === currentSet;
    } };
  }, [voiceOwnerId, captureVoiceOwner, getMountedRun, currentExerciseIndex, currentSet, runtimeLifecycle]);

  const startedAt = useRef(new Date(runtimeState.startedAt));
  startedAt.current = new Date(runtimeState.startedAt);

  // Execution defaults and carry-over are derived by the shared controller.

  /* Derived */
  const currentEx = exercises[currentExerciseIndex];
  const totalSets = exercises.reduce((s, e) => s + e.sets.length, 0);
  const doneSets = exercises.reduce((s, e) => s + e.sets.filter(st => st.done).length, 0);
  const exercisesDone = exercises.filter(e => e.sets.every(s => s.done)).length;
  const progressPct = totalSets > 0 ? (doneSets / totalSets) * 100 : 0;

  /* Media batch */
  const exerciseNames = useMemo(() => exercises.map(e => fitnessExercisePresentationName(e.name, e.muscle)), [exercises]);
  const fallbackGifs = useMemo(() => {
    const map: Record<string, string> = {};
    exercises.forEach(e => { if (e.gifUrl) map[fitnessExercisePresentationName(e.name, e.muscle)] = e.gifUrl; });
    return map;
  }, [exercises]);
  const { mediaMap, loading: mediaLoading, error: mediaError, retry: retryMedia } = useExerciseMediaBatch(exerciseNames, fallbackGifs);

  const retryWorkoutLoad = useCallback(() => {
    const ownerId = identity.userId;
    if (ownerId && fitnessWorkoutController.getOwner() === ownerId && !fitnessWorkoutController.getOwned(ownerId)) {
      fitnessWorkoutController.start(ownerId);
    }
    setLoadError(false); setIsLoading(true); setLoadAttempt(value => value + 1);
  }, [identity.userId]);

  const retryDraftPersistence = useCallback(async () => {
    const ownerId = checkpointOwnerId;
    if (!ownerId || draftRetrying) return;
    const current = captureCheckpointOwner(), action = ++persistenceAction.current;
    const retryHydration = loadError || draftPersistence.operation === "hydrate";
    setDraftRetrying(true);
    try {
      await retryWorkoutDraftPersistence(ownerId, current);
      if (current() && action === persistenceAction.current && retryHydration) retryWorkoutLoad();
    } catch {
      // The persistence store keeps the failure visible. Retry never repeats a set or reward.
    } finally {
      if (current() && action === persistenceAction.current) setDraftRetrying(false);
    }
  }, [checkpointOwnerId, captureCheckpointOwner, draftRetrying, draftPersistence.operation, loadError, retryWorkoutLoad]);

  /* ─── Load workout plan ─── */
  useEffect(() => {
    let alive = true;
    let loadDeadline: ReturnType<typeof setTimeout> | undefined;
    draftOwner.current = null;
    finalizing.current = null; setIsSaving(false);
    const load = async () => {
      if (!identity.userId) { setExercises([]); setShowResume(false); setIsLoading(false); return; }
      const accountChanged = lastResolvedOwner.current && lastResolvedOwner.current !== identity.userId;
      lastResolvedOwner.current = identity.userId;
      if (accountChanged && location.state?.freeWorkout) { setExercises([]); setShowResume(false); navigate("/fitness", { replace: true }); return; }
      const current = identity.capture();
      const assertCurrent = () => { if (!alive || !current()) throw new Error("Workout account changed"); };
      if (!runtimeRunId) {
        // A ready identity without its runtime must become an actionable failure,
        // rather than leaving an unbounded loading screen.
        setIsLoading(true); setLoadError(false); setShowResume(false);
        loadDeadline = setTimeout(() => {
          if (!alive || !current()) return;
          setLoadError(true); setIsLoading(false);
        }, FITNESS_RUNTIME_READY_TIMEOUT_MS);
        return;
      }
      const existingRuntime = fitnessWorkoutController.getOwned(identity.userId);
      if (existingRuntime?.state.exercises.length && existingRuntime.state.phase !== "complete") {
        draftOwner.current = identity.userId; setShowResume(false); setIsLoading(false); return;
      }
      if (existingRuntime?.state.phase === "complete") { fitnessWorkoutController.retire(); fitnessWorkoutController.start(identity.userId); return; }
      setIsLoading(true); setLoadError(false); setFinalizeError(false); setExercises([]); setSessionId(null);
      loadDeadline = setTimeout(() => {
        if (!alive || !current()) return;
        alive = false;
        setLoadError(true); setIsLoading(false);
        console.error("[FitnessWorkout] Workout read timed out");
      }, 20_000);
      let checkingCheckpoint = false;
      try {
        const { data: { user }, error: authError } = await supabase.auth.getUser();
        assertCurrent();
        if (authError || !user || user.id !== identity.userId) throw authError ?? new Error("Workout requires sign-in");
        checkingCheckpoint = true;
        await ensureWorkoutDraftHydrated(user.id, () => alive && current());
        assertCurrent();
        checkingCheckpoint = false;
        const live = fitnessWorkoutController.getOwned(user.id);
        if (!live || live.runId !== runtimeRunId) { setLoadError(true); setIsLoading(false); return; }
        // A concurrent route/runtime action wins over an older hydration result.
        if (live.state.exercises.length) { draftOwner.current = user.id; setShowResume(false); setIsLoading(false); return; }
        draftOwner.current = user.id;
        const saved = getWorkoutDraft(user.id);
        if (saved) { setShowResume(true); setIsLoading(false); return; }
        sessionKeyRef.current = createFitnessId(); runtimeField("clientId", sessionKeyRef.current);
        startedAt.current = new Date(); runtimeField("startedAt", startedAt.current.toISOString());
        setCurrentExerciseIndex(0); setCurrentSet(1); setActualSetValues({}); setRestDeadline(null); setXpTotal(0);

        // ─── FREE WORKOUT BRANCH ─────────────────────────────
        const routeState: unknown = location.state;
        const freeState = routeState && typeof routeState === "object" && "freeWorkout" in routeState && routeState.freeWorkout === true ? routeState : null;

        if (freeState) {
          if ("expectedUserId" in freeState && freeState.expectedUserId !== user.id) { navigate("/fitness", { replace: true }); return; }
          const mapped = prepareFreeWorkoutExercises("exercises" in freeState ? freeState.exercises : undefined);
          if (!mapped.length) { navigate("/fitness/free-workout", { replace: true }); return; }
          const uniqueGroups = Array.from(new Set(mapped.map(e => e.muscle)));
          const groupLabels = uniqueGroups
            .map(g => {
              const key = MUSCLE_GROUP_MAP[g.toLowerCase()];
              return key ? t(key) : g;
            })
            .join(", ");
          const dynamicName = tf("fitness.freeWorkout.sessionNameTemplate", { groups: groupLabels });
          let routineId: string | null = null;
          if ("routineId" in freeState && typeof freeState.routineId === "string") {
            const owned = await supabase.from("workout_plans").select("id").eq("id", freeState.routineId).eq("user_id", user.id).maybeSingle();
            assertCurrent();
            if (owned.error || !owned.data) throw new Error("Routine unavailable");
            routineId = owned.data.id;
          }
          setWorkoutName("workoutName" in freeState && typeof freeState.workoutName === "string" ? freeState.workoutName : dynamicName);
          setPlanId(routineId);
          if ("guided" in freeState && freeState.guided === true) runtimeField("guidedPreparation", createFitnessGuidedPreparation());

          setExercises(mapped);

          const initialConfigs: Record<number, ExerciseConfig> = {};
          mapped.forEach((ex, i) => {
            initialConfigs[i] = {
              weight: ex.defaultWeight,
              reps: ex.defaultReps,
              sets: ex.sets.length,
            };
          });
          setExerciseConfigs(initialConfigs);

          setPhase("setup");
          clearFreeWorkoutSetup(); clearFreeWorkoutOrder(); clearFreeWorkoutBuilder(user.id);
          setIsLoading(false);
          return;
        }

        // ─── PLAN BRANCH (unchanged from current behavior) ───
        const { data: plan, error: planError } = await supabase
          .from("workout_plans").select("*")
          .eq("user_id", user.id).eq("is_active", true).order("created_at", { ascending: false }).limit(1).maybeSingle();

        assertCurrent();
        if (planError) throw planError;
        if (!plan) { navigate("/fitness/guided"); return; }
        const days = readWorkoutPlanDays(plan.days);
        if (!days.length) { navigate("/fitness/guided"); return; }

        setPlanId(plan.id);
        const { data: previous, error: previousError } = await supabase.from("workout_sessions")
          .select("name,exercises").eq("user_id", user.id).eq("plan_id", plan.id)
          .not("completed_at", "is", null).order("completed_at", { ascending: false }).limit(1);
        assertCurrent(); if (previousError) throw previousError;
        const today: WorkoutPlanDayInput | null = nextPlanDay({ ...plan, days }, previous?.[0]);
        if (!today?.exercises?.length) { navigate("/fitness/guided"); return; }

        setWorkoutName(today.name || plan.name);

        // Fetch last weights for each exercise
        const exNames = today.exercises.map(e => e.name).filter(Boolean);
        const lastWeights: Record<string, number> = {};
        if (exNames.length) {
          const { data: logs } = await supabase
            .from("exercise_logs")
            .select("exercise_name, sets")
            .eq("user_id", user.id)
            .in("exercise_name", exNames)
            .order("created_at", { ascending: false })
            .limit(exNames.length * 2);
          assertCurrent();
          if (logs) {
            for (const log of logs) {
              if (lastWeights[log.exercise_name] === undefined) {
                const weight = readLastWorkoutWeight(log.sets);
                if (weight !== null) lastWeights[log.exercise_name] = weight;
              }
            }
          }
        }

        const mapped = today.exercises.map(exercise => preparePlannedWorkoutExercise(exercise, lastWeights[exercise.name]));

        setExercises(mapped);

        const initialConfigs: Record<number, ExerciseConfig> = {};
        mapped.forEach((ex, i) => {
          initialConfigs[i] = {
            weight: ex.defaultWeight,
            reps: ex.defaultReps,
            sets: ex.sets.length,
          };
        });
        setExerciseConfigs(initialConfigs);

        setPhase("setup");
        setIsLoading(false);
      } catch {
        if (!alive || !current()) return;
        console.error("[FitnessWorkout] Failed to load workout");
        if (checkingCheckpoint) {
          // A failed durable read is not proof that no saved workout exists.
          setShowResume(false); setLoadError(true); setIsLoading(false);
        } else {
          toast.error(t("fitness.workout.loadError"));
          navigate("/fitness");
        }
      } finally { clearTimeout(loadDeadline); }
    };
    void load();
    return () => { alive = false; clearTimeout(loadDeadline); };
  }, [location.key, identity.userId, runtimeRunId, loadAttempt]); // eslint-disable-line

  // Draft persistence belongs to the same controller on every route.

  /* ─── Resume handler ─── */
  const handleResume = useCallback(() => {
    const mounted = getMountedRun();
    if (!mounted || mounted.busy) return;
    if (mounted.state.exercises.length) { setShowResume(false); setIsLoading(false); return; }
    const saved = identity.userId ? getWorkoutDraft(identity.userId) : null;
    if (!saved) { setShowResume(false); retryWorkoutLoad(); return; }
    if (identity.userId) {
      const restoredPhase = saved.phase === "countdown" ? "execution" : saved.phase;
      const state: FitnessWorkoutState = {
        ...(saved.guidedPreparation ? { guidedPreparation: saved.guidedPreparation } : {}),
        exercises: saved.exercises, workoutName: saved.workoutName || "", planId: saved.planId || null, sessionId: saved.sessionId || null,
        exerciseConfigs: saved.exerciseConfigs || {}, currentExerciseIndex: saved.currentExerciseIndex ?? 0, currentSet: saved.currentSet ?? 1,
        startedAt: saved.startedAt, actualSetValues: saved.actualSetValues || Object.fromEntries(saved.exercises.map((ex: ExerciseData, index: number) => [index, ex.sets.filter(set => set.done).map(set => ({ weight: set.weight, reps: set.reps }))])),
        editingWeight: saved.editingWeight ?? 0, editingReps: saved.editingReps ?? 0, restDeadline: saved.restDeadline ?? null, restDuration: saved.restDuration ?? 60,
        clientId: saved.clientId || createFitnessId(), xpTotal: saved.xpTotal ?? 0, phase: ["setup", "execution", "rest", "exercise-complete"].includes(restoredPhase) ? restoredPhase : "setup",
      };
      fitnessWorkoutController.restore(identity.userId, state);
      analytics.fitnessResume();
    }
    setShowResume(false);
    setIsLoading(false);
  }, [identity.userId, getMountedRun, retryWorkoutLoad]);

  /* ═══════════════════════════════════════════════
     NEW STATE MACHINE — Transition Functions (Part 1B)
     ═══════════════════════════════════════════════ */

  /** Setup complete → save config, sync exercise sets, start countdown */
  const handleSetupComplete = useCallback((config: ExerciseConfig) => {
    const mounted = getMountedRun();
    if (!mounted || mounted.state.phase !== "setup" || isGuidedPreparationBlocking(mounted.state.guidedPreparation) || mounted.paused || mounted.busy
      || mounted.state.currentExerciseIndex !== currentExerciseIndex || mounted.state.currentSet !== currentSet) return;
    setExerciseConfigs(prev => ({ ...prev, [currentExerciseIndex]: config }));
    setExercises(prev => {
      const next = [...prev];
      const ex = { ...next[currentExerciseIndex], sets: [...next[currentExerciseIndex].sets] };
      const newSets: SetData[] = Array.from({ length: config.sets }, (_, i) =>
        ex.sets[i]?.done ? ex.sets[i] : { ...ex.sets[i], weight: config.weight, reps: config.reps, done: false }
      );
      ex.sets = newSets;
      next[currentExerciseIndex] = ex;
      return next;
    });
    setPhase("countdown");
  }, [currentExerciseIndex, currentSet, getMountedRun, setExerciseConfigs, setExercises, setPhase]);

  /** Countdown complete → enter execution phase */
  const handleCountdownComplete = useCallback(() => {
    const mounted = getMountedRun();
    if (!mounted || mounted.state.phase !== "countdown" || mounted.paused || mounted.busy
      || mounted.state.currentExerciseIndex !== currentExerciseIndex || mounted.state.currentSet !== currentSet) return;
    setPhase("execution");
  }, [getMountedRun, currentExerciseIndex, currentSet, setPhase]);

  /** Only the controller confirms the visible set and advances its schedule. */
  const handleSetDone = useCallback(async () => {
    const mounted = getMountedRun(), ownerId = identity.userId;
    if (!mounted || !ownerId || mounted.state.phase !== "execution" || mounted.paused || mounted.busy || setDoneInFlightRef.current) return;
    setDoneInFlightRef.current = true;
    const ownerCurrent = identity.capture();
    let releaseReward: (() => void) | null = null;
    try {
      const snapshot = fitnessWorkoutController.getSetCompletionSnapshot(ownerId);
      if (snapshot.runId !== mounted.runId || snapshot.epoch !== mounted.epoch
        || snapshot.currentExerciseIndex !== currentExerciseIndex || snapshot.currentSet !== currentSet) return;
      const receipt = fitnessWorkoutController.completeSetCAS(snapshot);
      if (!fitnessWorkoutController.setCompletionReceiptMatches(receipt)) return;
      try { navigator.vibrate?.(50); } catch { /* Haptics are optional. */ }
      releaseReward = fitnessWorkoutController.acquireBusy(ownerId, "set_done");
      await addXP("fitness", "set_completed", 10).then(reward => {
        const live = getMountedRun();
        if (ownerCurrent() && live?.runId === mounted.runId && live.lifecycle === mounted.lifecycle && reward.inserted) {
          fitnessWorkoutController.patch(ownerId, state => ({ xpTotal: state.xpTotal + 10 }));
        }
      }).catch(error => console.warn("[FitnessWorkout] Set reward remains unconfirmed", error));
      // Completion remains explicit. The checkpoint status separately reports
      // durable acknowledgment; retrying storage never repeats this set or XP.
    } catch (error) {
      if (ownerCurrent() && getMountedRun()) {
        console.error("[FitnessWorkout] Set completion was not confirmed", error);
        toast.error(t("fitness.builder.workoutChanged"));
      }
    } finally { releaseReward?.(); setDoneInFlightRef.current = false; }
  }, [getMountedRun, identity, currentExerciseIndex, currentSet, addXP, t]);

  /** Rest complete → new countdown for next set */
  const handleRestComplete = useCallback(() => {
    const mounted = getMountedRun();
    if (!mounted || mounted.state.phase !== "rest" || mounted.paused || mounted.busy
      || mounted.state.currentExerciseIndex !== currentExerciseIndex || mounted.state.currentSet !== currentSet) return;
    setPhase("countdown");
  }, [getMountedRun, currentExerciseIndex, currentSet, setPhase]);

  /** Exercise complete → next exercise setup or finalize workout */
  const handleExerciseCompleteNext = async () => {
    const mounted = getMountedRun();
    if (!mounted || mounted.state.phase !== "exercise-complete" || mounted.paused || mounted.busy
      || mounted.state.currentExerciseIndex !== currentExerciseIndex || mounted.state.currentSet !== currentSet) return;
    const transition = getWorkoutSetTransition(mounted.state.exercises, {
      exerciseIndex: mounted.state.currentExerciseIndex, setNumber: mounted.state.currentSet,
    });
    if (transition.next) {
      fitnessWorkoutController.patch(mounted.ownerId, {
        currentExerciseIndex: transition.next.exerciseIndex, currentSet: transition.next.setNumber, phase: "setup",
      });
    } else {
      await finalizeWorkout();
    }
  };

  const handleWorkoutFinish = useCallback(() => navigate("/fitness"), [navigate]);

  /* A confirmed write is the only transition to completion. */
  const finalizeWorkout = async () => {
    const mounted = getMountedRun();
    if (finalizing.current || !mounted || mounted.state.phase !== "exercise-complete" || mounted.paused || mounted.busy || !identity.userId) return;
    const ownerCurrent = identity.capture();
    const current = () => { const live = getMountedRun(); return ownerCurrent() && !!live && live.runId === mounted.runId && live.epoch === mounted.epoch && live.lifecycle === mounted.lifecycle; };
    if (!current()) return;
    const operation = createFitnessId();
    finalizing.current = operation; setIsSaving(true); setFinalizeError(false);
    try {
      const options: FitnessWorkoutFinishOptions = { ownerId: identity.userId, auth: supabase.auth, isCurrent: current, mirrorContext: null, addXP };
      const prepared = await prepareFitnessWorkoutFinish(options);
      if (!current()) return;
      const receipt = await confirmFitnessWorkoutFinish(prepared);
      if (!current()) return;
      if (!receipt.localCompleted || !receipt.draftRemoved) throw new Error("Workout local completion not confirmed");
      finishAcknowledgment.current = { options, receipt, started: false };
      setFinishReceipt(receipt);
      analytics.fitnessCompleted(); sfx.success("module");
    } catch (error) {
      if (current()) { console.error("[Fitness] completion save failed", error); setFinalizeError(true); toast.error(t("fitnessWorkoutFinish.unknown")); }
    } finally {
      if (finalizing.current === operation) finalizing.current = null;
      if (current()) setIsSaving(false);
    }
  };

  // The success screen's committed React effect acknowledges the original
  // durable operation. Lookup/ACK never repeats the save, log index or reward.
  useEffect(() => {
    const pending = finishAcknowledgment.current;
    if (phase !== "complete" || !pending || pending.started || sessionId !== pending.receipt.workoutId || !pending.options.isCurrent()) return;
    pending.started = true;
    void acknowledgeFitnessWorkoutFinishReceipt(pending.options, pending.receipt).catch(error => {
      console.warn("[Fitness] saved workout acknowledgment remains pending", error);
    });
  }, [phase, sessionId, finishReceipt]);

  const handleTogglePause = useCallback(() => {
    const mounted = getMountedRun();
    if (!identity.userId || !mounted || mounted.busy) return;
    if (mounted.paused) fitnessWorkoutController.resumeInUI(identity.userId);
    else fitnessWorkoutController.pauseForClose(identity.userId);
  }, [identity.userId, getMountedRun]);
  const handleClose = useCallback(async () => {
    const ownerId = checkpointOwnerId, mounted = getMountedRun();
    if (!ownerId || !mounted || closeInFlight.current || finalizing.current || !fitnessWorkoutController.pauseForClose(ownerId)) return;
    const pausedRun = fitnessWorkoutController.getOwned(ownerId);
    if (!pausedRun || pausedRun.runId !== mounted.runId || pausedRun.epoch !== mounted.epoch) return;
    const ownerCurrent = captureCheckpointOwner();
    const current = () => {
      const live = fitnessWorkoutController.getOwned(ownerId);
      return ownerCurrent() && live?.runId === pausedRun.runId && live.epoch === pausedRun.epoch
        && live.lifecycle === pausedRun.lifecycle && live.paused;
    };
    const action = ++persistenceAction.current;
    closeInFlight.current = true; setClosingCheckpoint(true);
    try {
      await flushWorkoutDraftPersistence(ownerId, current);
      if (current() && action === persistenceAction.current) navigate("/fitness");
    } catch {
      // Stay paused with the store's failed/retry status until durable ACK.
    } finally {
      if (action === persistenceAction.current) {
        closeInFlight.current = false;
        if (ownerCurrent()) setClosingCheckpoint(false);
      }
    }
  }, [navigate, checkpointOwnerId, captureCheckpointOwner, getMountedRun]);

  // Countdown/rest/automatic-next transitions belong only to the controller.

  /* ─── Wake Lock: keep screen on during execution and rest ─── */
  useEffect(() => {
    let wakeLock: WakeLockSentinel | null = null;
    let released = false;
    let acquiring = false;

    const shouldLock = !paused && (phase === "execution" || phase === "rest");

    const acquire = async () => {
      if (released || acquiring || wakeLock) return;
      acquiring = true;
      try {
        if ("wakeLock" in navigator) {
          const acquired = await navigator.wakeLock.request("screen");
          if (released) { await acquired.release(); return; }
          wakeLock = acquired;
          acquired.addEventListener("release", () => {
            if (wakeLock === acquired) wakeLock = null;
          });
        }
      } catch {
        // Optional screen wake lock may be denied or unsupported.
      } finally { acquiring = false; }
    };

    const release = async () => {
      released = true;
      try {
        await wakeLock?.release?.();
      } catch { /* The browser may already have released this optional lock. */ }
      wakeLock = null;
    };

    const handleVisibility = () => {
      if (document.visibilityState === "visible" && shouldLock && !wakeLock) {
        acquire();
      }
    };

    if (shouldLock) {
      acquire();
      document.addEventListener("visibilitychange", handleVisibility);
    }

    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      release();
    };
  }, [phase, paused]);

  /* ─── Display helpers ─── */
  const canonicalExerciseName = currentEx ? fitnessExercisePresentationName(currentEx.name, currentEx.muscle) : "";
  const exerciseDisplayName = currentEx
    ? (currentEx.name.startsWith("fitness.") ? t(currentEx.name) : localizeExerciseName(canonicalExerciseName, t, lang))
    : "";
  const muscleKey = currentEx ? MUSCLE_GROUP_MAP[currentEx.muscle.toLowerCase()] : null;
  const muscleDisplayLabel = currentEx
    ? (muscleKey ? t(muscleKey) : (currentEx.muscle.startsWith("fitness.") ? t(currentEx.muscle) : currentEx.muscle))
    : "";

  /* ─── Completion stats ─── */
  const totalVolume = getGymVolumeKg({ id: sessionId || sessionKeyRef.current, name: workoutName, source: "manual", exercises });
  const totalTimeMin = Math.round((workoutRuntime.runtime?.activeElapsedMs ?? 0) / 60000);

  if (identity.status !== "ready") return <MobileLayout className="pb-28 pt-8"><FitnessDataState status={identity.status} busy={identity.isRefreshing} onRetry={identity.refresh} /></MobileLayout>;

  if (loadError) return <MobileLayout className="pb-28 pt-8">
    {draftPersistence.status === "failed"
      ? <WorkoutDraftPersistenceStatus snapshot={draftPersistence} busy={draftRetrying} onRetry={() => void retryDraftPersistence()} />
      : <FitnessDataState status="error" onRetry={retryWorkoutLoad} />}
  </MobileLayout>;

  /* ─── Loading state ─── */
  if (isLoading && !showResume) {
    return (
      <MobileLayout className="pb-28 pt-8 flex items-center justify-center">
        <div className="w-full px-4">
          <WorkoutDraftPersistenceStatus snapshot={draftPersistence} busy={draftRetrying} onRetry={() => void retryDraftPersistence()} />
          <FitnessDataState status="loading" busy onRetry={retryWorkoutLoad} />
        </div>
      </MobileLayout>
    );
  }

  /* ─── Resume prompt ─── */
  if (showResume) {
    return (
      <MobileLayout className="pb-28 pt-8 flex flex-col items-center justify-center px-6">
        <div className="w-full max-w-sm rounded-2xl p-6 text-center" style={{ background: "hsl(var(--card))", border: "1px solid hsl(187 72% 50% / 0.25)" }}>
          <WorkoutDraftPersistenceStatus snapshot={draftPersistence} busy={draftRetrying} onRetry={() => void retryDraftPersistence()} className="mb-4 text-left" />
          <p className="font-display text-[18px] font-bold text-foreground mb-2">{t("fitness.workout.continuePrompt")}</p>
          <p className="text-[12px] font-body text-foreground/50 mb-5">{t("fitness.workout.resumeDesc")}</p>
          <button onClick={handleResume}
            className="w-full py-3 rounded-xl font-display text-[13px] font-semibold tracking-wide mb-3 active:scale-[0.97]"
            style={{ background: "hsl(var(--primary))", color: "hsl(var(--background))" }}>
            {t("fitness.workout.continueBtn")}
          </button>
          <WorkoutCancelButton ownerId={identity.userId!} onDiscard={() => navigate("/fitness/free-workout", { replace: true })} className="w-full" />
        </div>
      </MobileLayout>
    );
  }

  return (
    <MobileLayout className="pb-28 pt-0 relative overflow-hidden lg:max-w-7xl" noAnimation style={{ background: "transparent", paddingTop: "max(1rem, env(safe-area-inset-top))" }}>
      {/* Immersive background */}
      <div className="fixed inset-0 pointer-events-none" style={{ zIndex: 0 }}>
        <div className="absolute inset-0" style={{ backgroundImage: `url(${SB_FITNESS_BG})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(10,10,15,0.88) 0%, rgba(10,10,15,0.92) 50%, rgba(10,10,15,0.95) 100%)" }} />
      </div>

      <div className="relative flex flex-col h-full" style={{ zIndex: 1 }}>
        {finalizeError && <div role="alert" className="m-4 rounded-xl border border-primary/30 bg-card p-4 text-sm"><p>{t("fitnessWorkoutFinish.unknown")}</p><button type="button" onClick={finalizeWorkout} disabled={isSaving} className="min-h-11 text-primary underline">{t("common.retry")}</button></div>}
        {isSaving && <p role="status" className="p-3 text-center text-sm text-muted-foreground">{t("agenda.saving")}</p>}
        {phase !== "countdown" && (
          <>
            {/* Header */}
            <div className="flex items-center justify-between pl-4 pr-28 pt-2 pb-2">
              <div className="flex-1 min-w-0">
                <p className={(phase === "execution" ? "hidden" : "") + " text-[9px] font-display tracking-[0.2em] uppercase"} style={{ color: "hsl(187 72% 50% / 0.55)" }}>
                  {t("fitness.workout.title")}
                </p>
                <p className="font-display text-[16px] font-semibold text-foreground truncate">
                  {workoutName.startsWith("fitness.") ? t(workoutName) : workoutName}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <p className="font-display text-[12px] font-medium" style={{ color: "hsl(218 14% 55%)" }}>
                  {totalTimeMin} {t("fitness.min")}
                </p>

              </div>
            </div>

            {/* Progress bar */}
            <div className="px-4 mb-1">
              <div className="w-full h-1.5 rounded-full" style={{ background: "hsl(222 18% 14%)" }}>
                <motion.div
                  className="h-full rounded-full"
                  animate={{ width: `${progressPct}%` }}
                  transition={{ duration: 0.5 }}
                  style={{ background: "linear-gradient(90deg, hsl(187 72% 50%), hsl(140 60% 50%))" }}
                />
              </div>
            </div>
          </>
        )}

        {phase !== "complete" && phase !== "countdown" && phase !== "rest" && <WorkoutDraftPersistenceStatus snapshot={draftPersistence}
          busy={draftRetrying || closingCheckpoint} onRetry={() => void retryDraftPersistence()} className="mx-4 mb-2" />}

        {/* Exercise indicator dots */}
        {currentEx && phase === "setup" && (
          <div className="flex items-center justify-center gap-1.5 py-2">
            {exercises.map((ex, i) => {
              const done = ex.sets.every(s => s.done);
              const active = i === currentExerciseIndex;
              return (
                <motion.div
                  key={i}
                  className="rounded-full"
                  animate={{
                    width: active ? 16 : 6,
                    height: 6,
                    boxShadow: done ? "0 0 8px hsl(140 60% 50% / 0.5)" : "none",
                  }}
                  transition={{ duration: 0.3 }}
                  style={{
                    background: done
                      ? "hsl(140 60% 50%)"
                      : active
                        ? "hsl(187 72% 50%)"
                        : "hsl(222 18% 20%)",
                    borderRadius: active ? 3 : "50%",
                  }}
                />
              );
            })}
          </div>
        )}

        {currentEx && phase === "setup" && preparingGuided && guidedPreparation && <GuidedWorkoutPreparation
          value={guidedPreparation} scopeId={JSON.stringify([identity.userId, runtimeRunId, runtimeEpoch, runtimeLifecycle])}
          paused={paused || runtimeBusy} onAction={handleGuidedPreparation}
          instructorPreview={<ExerciseDemonstration exerciseId={canonicalExerciseName} exerciseName={exerciseDisplayName} instructorId={instructor.instructorId} paused variant="stage" />}
          instructorSelector={<FitnessInstructorSelector value={instructor.instructorId} onChange={id => void instructor.saveInstructor(id)} disabled={paused || runtimeBusy} busy={instructor.busy} error={instructor.error} onRetry={instructor.retry} />}
        />}

        {/* ═══ SETUP PHASE ═══ */}
        {currentEx && phase === "setup" && !paused && !preparingGuided && (
          <WorkoutSetup
            timed={currentEx.timed}
            exerciseName={exerciseDisplayName}
            muscleGroup={muscleDisplayLabel}
            defaultWeight={exerciseConfigs[currentExerciseIndex]?.weight ?? currentEx.defaultWeight}
            defaultReps={exerciseConfigs[currentExerciseIndex]?.reps ?? currentEx.defaultReps}
            defaultSets={exerciseConfigs[currentExerciseIndex]?.sets ?? currentEx.sets.length}
            onComplete={handleSetupComplete}
          />
        )}

        {/* ═══ EXECUTION PHASE ═══ */}
        {currentEx && phase === "execution" && (!paused || (currentEx.timed && fitnessIntervalKind(currentEx.name) !== null)) && (
          <WorkoutExecution
            instructorId={instructor.instructorId}
            instructorSelector={<FitnessInstructorSelector value={instructor.instructorId} onChange={id => void instructor.saveInstructor(id)} disabled={paused || runtimeBusy} busy={instructor.busy} error={instructor.error} onRetry={instructor.retry} />}
            sessionTimeline={<FitnessWorkoutSequence exercises={exercises} currentIndex={currentExerciseIndex} />}
            setMetadata={<FitnessSetMetadataInput set={currentEx.sets[currentSet - 1]} disabled={paused || runtimeBusy}
              onChange={metadata => {
                const live = getMountedRun();
                if (!identity.userId || !live || live.paused || live.busy || live.state.phase !== "execution"
                  || live.state.currentExerciseIndex !== currentExerciseIndex || live.state.currentSet !== currentSet) return;
                fitnessWorkoutController.patch(identity.userId, state => ({
                  exercises: state.exercises.map((exercise,index) => index !== currentExerciseIndex ? exercise : {
                    ...exercise, sets: exercise.sets.map((set,slot) => {
                      if (slot !== currentSet - 1 || set.done) return set;
                      const updated = { ...set, type: metadata.type }; delete updated.rir;
                      return metadata.rir === undefined ? updated : { ...updated, rir: metadata.rir };
                    }),
                  }),
                }));
              }} />}
            exerciseActions={<div className="space-y-4">
              {identity.userId && <FitnessExerciseNotes key={canonicalExerciseName} ownerId={identity.userId}
                exerciseName={canonicalExerciseName} muscle={currentEx.muscle} disabled={paused || runtimeBusy}
                onSaved={notes => {
                  const live = getMountedRun();
                  if (!identity.userId || !live || live.paused || live.busy || live.state.phase !== "execution"
                    || live.state.currentExerciseIndex !== currentExerciseIndex) return;
                  fitnessWorkoutController.patch(identity.userId, state => ({
                    exercises: state.exercises.map((exercise,index) => index === currentExerciseIndex ? { ...exercise, notes } : exercise),
                  }));
                }} />}
              <WorkoutExerciseReplacement currentName={currentEx.name} muscle={currentEx.muscle} disabled={paused || runtimeBusy}
                onChoose={choice => {
                  const live = getMountedRun();
                  if (!identity.userId || !live || live.paused || live.busy || live.state.phase !== "execution"
                    || live.state.currentExerciseIndex !== currentExerciseIndex || live.state.currentSet !== currentSet) return;
                  try {
                    const updated = replaceCurrentWorkoutExercise(live.state, { name: choice.name, muscle: choice.muscle, timed: choice.timed, defaultReps: choice.defaultReps, defaultWeight: choice.defaultWeight, restSeconds: choice.restSeconds });
                    fitnessWorkoutController.patch(identity.userId, updated);
                    toast.info(experienceCopy.reviewTargets);
                  } catch (error) {
                    console.error("[FitnessWorkout] Exercise replacement rejected", error); toast.error(experienceCopy.replaceError);
                  }
                }} />
            </div>}
            timed={currentEx.timed}
            exerciseName={exerciseDisplayName}
            exerciseId={fitnessExercisePresentationIdentity(currentEx.name, currentEx.muscle)}
            speechScopeId={JSON.stringify([identity.userId, runtimeRunId, runtimeEpoch])}
            muscleGroup={muscleDisplayLabel}
            media={mediaMap[canonicalExerciseName]}
            instructions={getExerciseByName(canonicalExerciseName)?.instructions}
            mediaLoading={mediaLoading}
            mediaError={mediaError}
            onMediaRetry={retryMedia}
            paused={paused || runtimeBusy}
            nextExerciseName={(() => {
              const next = getWorkoutSetTransition(exercises, { exerciseIndex: currentExerciseIndex, setNumber: currentSet }).next;
              const exercise = next ? exercises[next.exerciseIndex] : null;
              return exercise ? (exercise.name.startsWith("fitness.") ? t(exercise.name) : localizeExerciseName(fitnessExercisePresentationName(exercise.name, exercise.muscle), t, lang)) : undefined;
            })()}
            currentSet={currentSet}
            totalSets={exerciseConfigs[currentExerciseIndex]?.sets ?? currentEx.sets.length}
            weight={editingWeight}
            reps={editingReps}
            previousPerformance={identity.userId ? <WorkoutPreviousSets ownerId={identity.userId} exerciseName={canonicalExerciseName} muscle={currentEx.muscle} currentSet={currentSet} timed={!!currentEx.timed} disabled={paused || runtimeBusy} onApply={values => {
              const live = getMountedRun();
              if (identity.userId && live && !live.paused && !live.busy && live.state.phase === "execution" && live.state.currentExerciseIndex === currentExerciseIndex && live.state.currentSet === currentSet) fitnessWorkoutController.patch(identity.userId, { editingWeight: values.weight, editingReps: values.reps });
            }} /> : undefined}
            onValuesChange={values => {
              const mounted = getMountedRun();
              if (!identity.userId || !mounted || mounted.paused || mounted.busy || mounted.state.phase !== "execution") return;
              fitnessWorkoutController.patch(identity.userId, { editingWeight: values.weight, editingReps: values.reps });
            }}
            onSetDone={handleSetDone}
            onEditClick={() => { if (!paused && !runtimeBusy) setShowEditSheet(true); }}
            intervalScopeId={JSON.stringify([identity.userId, runtimeRunId, runtimeEpoch, runtimeLifecycle, currentExerciseIndex, currentSet])}
            onTimedTargetApply={seconds => {
              const mounted = getMountedRun();
              if (!mounted || mounted.lifecycle !== runtimeLifecycle || mounted.paused || mounted.busy
                || mounted.state.phase !== "execution" || mounted.state.currentExerciseIndex !== currentExerciseIndex
                || mounted.state.currentSet !== currentSet || !mounted.state.exercises[currentExerciseIndex]?.timed
                || !Number.isSafeInteger(seconds) || seconds < 1 || seconds > FITNESS_INTERVAL_LIMITS.maxTotalSeconds) return false;
              setEditingReps(seconds);
              return getMountedRun()?.state.editingReps === seconds;
            }}
          />
        )}

        {/* ═══ EXERCISE COMPLETE ═══ */}
        {currentEx && phase === "exercise-complete" && (
          <button type="button"
            className="flex-1 flex flex-col items-center justify-center px-6 cursor-pointer"
            onClick={handleExerciseCompleteNext} disabled={isSaving || paused || runtimeBusy}
          >
            <motion.div
              className="text-6xl mb-4"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5, type: "spring" }}
              style={{ color: "hsl(140 60% 55%)" }}
            >
              &#10003;
            </motion.div>
            <p className="font-display text-2xl font-bold text-white mb-2">
              {exerciseDisplayName}
            </p>
            <p className="text-xl font-display mb-3" style={{ color: "hsl(187 72% 55%)" }}>
              {t("fitness.workout.exerciseComplete")}
            </p>
            <ol className="mb-3 space-y-1 text-base text-muted-foreground">
              {currentEx.sets.filter(set => set.done).map((set, index) => <li key={index}>
                {index + 1}. {set.weight} {t("fitness.weightUnit")} × {set.reps} {t(currentEx.timed ? "fitness.ux.seconds" : "fitness.workout.reps")}
              </li>)}
            </ol>
            <span className="mt-6 min-h-11 rounded-full border border-primary px-6 py-3">{t(getWorkoutSetTransition(exercises, { exerciseIndex: currentExerciseIndex, setNumber: currentSet }).next ? "fitness.workout.nextExercise" : "fitness.workout.finishBtn")}</span>
          </button>
        )}
      </div>

      {(phase === "countdown" || phase === "rest") && <WorkoutDraftPersistenceStatus snapshot={draftPersistence}
        busy={draftRetrying || closingCheckpoint} onRetry={() => void retryDraftPersistence()}
        className="fixed inset-x-4 top-[calc(max(1rem,env(safe-area-inset-top))+3.5rem)] z-[60]" />}

      {phase !== "complete" && !showResume && <div className="fixed top-[max(1rem,env(safe-area-inset-top))] right-4 z-[60] flex gap-2">
        <button type="button" aria-label={t(paused ? "workoutRuntime.resume" : "workoutRuntime.pause")} disabled={runtimeBusy || isSaving || closingCheckpoint} onClick={handleTogglePause} className="min-h-11 min-w-11 rounded-xl border border-border bg-card flex items-center justify-center">
          {paused ? <Play size={18} /> : <Pause size={18} />}
        </button>
        <button type="button" aria-label={t("fitness.ux.pauseWorkout")} title={t("workoutRuntime.close")} disabled={runtimeBusy || isSaving || closingCheckpoint} onClick={() => void handleClose()} className="min-h-11 min-w-11 rounded-xl border border-border bg-card flex items-center justify-center"><X size={18} /></button>
      </div>}
      {identity.userId && phase !== "complete" && !showResume && <div className="relative z-10 px-4 text-center"><WorkoutCancelButton ownerId={identity.userId} onDiscard={() => navigate("/fitness/free-workout", { replace: true })} /></div>}
      {paused && <p role="status" className="relative z-10 m-4 rounded-xl border border-border bg-card p-4 text-center">{t("workoutRuntime.paused")}</p>}

      {/* Full-screen overlays */}
      <AnimatePresence>
        {phase === "countdown" && !paused && (
          <WorkoutCountdown controlledRemainingMs={workoutRuntime.runtime?.remainingMs ?? 0} onComplete={handleCountdownComplete} />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {phase === "rest" && !paused && (
          <WorkoutRestTimer
            controlledRemainingMs={workoutRuntime.runtime?.remainingMs ?? 0}
            defaultDuration={restDuration}
              deadline={restDeadline}
              onDeadlineChange={(deadline, duration) => { const mounted = getMountedRun(); if (mounted && !mounted.paused && !mounted.busy) { setRestDeadline(deadline); setRestDuration(duration); } }}
            onComplete={handleRestComplete}
            onSkip={handleRestComplete}
          />
        )}
      </AnimatePresence>

      {/* ═══ EDIT SET BOTTOMSHEET — peso/reps inline edit durante execution ═══ */}
      <Sheet open={showEditSheet && !paused && !runtimeBusy} onOpenChange={setShowEditSheet}>
        <SheetContent side="bottom" className="rounded-t-3xl" aria-describedby={undefined}>
          <SheetHeader>
            <SheetTitle>{t("fitness.workout.editSetTitle")}</SheetTitle>
          </SheetHeader>
          <div className="space-y-6 mt-6 pb-6">
            <WorkoutSetVoiceInput enabled={showEditSheet && phase === "execution" && !paused && !runtimeBusy}
              scopeId={JSON.stringify([identity.userId, runtimeRunId, runtimeEpoch, runtimeLifecycle, currentExerciseIndex, currentSet])}
              timed={!!currentEx?.timed} ownerGuard={voiceOwnerGuard}
              onApply={draft => {
                if (!voiceOwnerGuard?.isCurrent()) return;
                if (draft.weight !== undefined) setEditingWeight(draft.weight);
                if (draft.reps !== undefined) setEditingReps(draft.reps);
              }} />
            {/* PESO */}
            <div>
              <p className="text-sm mb-3 text-muted-foreground">{t("fitness.workout.weightLabel")}</p>
              <div className="flex items-center justify-center gap-4">
                <button
                  type="button"
                  onClick={() => { const mounted = getMountedRun(); if (mounted && !mounted.paused && !mounted.busy) setEditingWeight((p: number) => Math.max(0, p - 2.5)); }}
                  className="w-12 h-12 rounded-full bg-card border border-border text-xl active:scale-95 transition-transform"
                  aria-label="-2.5"
                >−</button>
                <span className="font-display text-3xl font-bold w-28 text-center tabular-nums">
                  {editingWeight}{t("fitness.weightUnit")}
                </span>
                <button
                  type="button"
                  onClick={() => { const mounted = getMountedRun(); if (mounted && !mounted.paused && !mounted.busy) setEditingWeight((p: number) => p + 2.5); }}
                  className="w-12 h-12 rounded-full bg-card border border-border text-xl active:scale-95 transition-transform"
                  aria-label="+2.5"
                >+</button>
              </div>
            </div>
            {/* REPS */}
            <div>
              <p className="text-sm mb-3 text-muted-foreground">{t(currentEx?.timed ? "fitness.ux.seconds" : "fitness.workout.reps")}</p>
              <div className="flex items-center justify-center gap-4">
                <button
                  type="button"
                  onClick={() => { const mounted = getMountedRun(); if (mounted && !mounted.paused && !mounted.busy) setEditingReps((p: number) => Math.max(1, p - 1)); }}
                  className="w-12 h-12 rounded-full bg-card border border-border text-xl active:scale-95 transition-transform"
                  aria-label="-1"
                >−</button>
                <span className="font-display text-3xl font-bold w-28 text-center tabular-nums">
                  {editingReps}
                </span>
                <button
                  type="button"
                  onClick={() => { const mounted = getMountedRun(); if (mounted && !mounted.paused && !mounted.busy) setEditingReps((p: number) => p + 1); }}
                  className="w-12 h-12 rounded-full bg-card border border-border text-xl active:scale-95 transition-transform"
                  aria-label="+1"
                >+</button>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowEditSheet(false)}
              className="w-full py-3 rounded-2xl bg-primary text-primary-foreground font-display font-semibold active:scale-95 transition-transform"
            >
              {t("common.confirm")}
            </button>
          </div>
        </SheetContent>
      </Sheet>

      {phase === "complete" && (
        <WorkoutCompletion
          totalTime={totalTimeMin}
          exercisesDone={exercisesDone}
          totalExercises={exercises.length}
          setsDone={doneSets}
          totalSets={totalSets}
          totalVolume={totalVolume}
          xpAwarded={xpTotal}
          workoutSessionId={sessionId}
          onFinish={handleWorkoutFinish}
        />
      )}


    </MobileLayout>
  );
};

export default FitnessWorkout;
